const _ = require('lodash');
const { Keystore, decrypt } = require('@chainsafe/bls-keystore');
const BLS = require('@chainsafe/bls/herumi');
const keygen = require('@chainsafe/bls-keygen');
const { phase0 } = require('@chainsafe/lodestar-types').ssz;
const { DOMAIN_DEPOSIT } = require('@chainsafe/lodestar-params');
const { customErrors, _extractChainID, _getChainSpecificConstants } = require('./constants');

const PATH = 'm/12381/60/0/0'; // BLS path
const AMOUNT = 32000000000; // pre-fixed validator activation amount in ETH2 units

const generateBLSSecretKey = () => keygen.generateRandomSecretKey();

const blsSecretKeyToPublic = (bls, privateKey) => bls.secretKeyToPublicKey(privateKey);

const generateEncryptedFileStorage = async (bls, privateKey, password) => {

    const publicKey = bls.secretKeyToPublicKey(privateKey);

    return Keystore.create(password, privateKey, publicKey, PATH);
};

// Compute signature for Beacon Chain processing
const computeSignature = async (signer, bls, secretKey) => {

    const chainID = await _extractChainID(signer);
    const { blsCredentialGeneratorConstants } = _getChainSpecificConstants(chainID);

    const publicKey = blsSecretKeyToPublic(bls, secretKey);

    // Form deposit root object
    const depositMessageRoot = phase0.DepositMessage.hashTreeRoot({
        pubkey: Buffer.from(publicKey),
        withdrawalCredentials: Buffer.from(blsCredentialGeneratorConstants.WITHDRAWAL_CREDENTIALS, 'hex'),
        amount: AMOUNT
    });

    // Necessary for context-dependence
    const forkDataRoot = phase0.ForkData.hashTreeRoot({
        currentVersion: Buffer.from(blsCredentialGeneratorConstants.FORK_VERSION, 'hex'),
        genesisValidatorsRoot: phase0.ForkData.defaultValue().genesisValidatorsRoot
    });

    // Form the Deposit domain structure in order to indicate deposit
    const domainArray = new Uint8Array(32);
    domainArray.set(DOMAIN_DEPOSIT);
    domainArray.set(forkDataRoot.subarray(0, 28), 4);
    const domain = Buffer.from(domainArray);

    // Form final context-dependent message root
    const domainedDepositRoot = phase0.SigningData.hashTreeRoot({
        objectRoot: depositMessageRoot, 
        domain
    });

    // Form the signature used to proof consent of deposit
    const signature = Buffer.from(bls.sign(secretKey, domainedDepositRoot)).toString('hex');

    const depositDataRoot = phase0.DepositData.hashTreeRoot({
        pubkey: Buffer.from(publicKey),
        withdrawalCredentials: Buffer.from(blsCredentialGeneratorConstants.WITHDRAWAL_CREDENTIALS, 'hex'),
        amount: AMOUNT,
        signature: Buffer.from(signature, 'hex')
    });

    return {
        signature,
        depositDataRoot: depositDataRoot.toString('hex'),
        depositMessageRoot: depositMessageRoot.toString('hex'),
    }
};

const _checkKeystorePass = (password) => {

	if (!password) {
	    throw customErrors.NULL_OR_UNDEFINED_VALUE;
	}

	return _.isString(password) && password.length >= 8;
};

const _getDepositDataFromKeystore = async (signer, keystore, password) => {

    if(!_checkKeystorePass(password)) {
        throw customErrors.PASSWORD_LT_8;
    }

    await BLS.init();
    const { bls } = BLS;

    const decryptedSigningKey = await decrypt(keystore, password);

    const { signature, depositDataRoot, depositMessageRoot } = await computeSignature(
        signer, bls, decryptedSigningKey
    );

    const chainID = await _extractChainID(signer);
    const { blsCredentialGeneratorConstants } = _getChainSpecificConstants(chainID);

    /// Deposit object, identical to what CLI returns
    return [{
        pubkey: keystore.pubkey,
        withdrawal_credentials: blsCredentialGeneratorConstants.WITHDRAWAL_CREDENTIALS,
        amount: AMOUNT,
        signature,
        deposit_message_root: depositMessageRoot,
        deposit_data_root: depositDataRoot,
        fork_version: blsCredentialGeneratorConstants.FORK_VERSION,
        network_name: blsCredentialGeneratorConstants.NETWORK_NAME,
        deposit_cli_version: blsCredentialGeneratorConstants.CLI_VERSION, /// Added to emulate deposit-cli
    }];
}

const _generateBLSCredentials = async (signer, password) => {

    if(!_checkKeystorePass(password)) {
        throw customErrors.PASSWORD_LT_8;
    }

    await BLS.init();
    const { bls } = BLS;

    // Generate BLS secret key
    const secretKey = generateBLSSecretKey();

    // Generate BLS Public Key
    const publicKey = blsSecretKeyToPublic(bls, secretKey);

    // Form signature and essential data details
    const { signature, depositDataRoot, depositMessageRoot } = await computeSignature(
        signer, bls, secretKey
    );

    const chainID = await _extractChainID(signer);
    const { blsCredentialGeneratorConstants } = _getChainSpecificConstants(chainID);

    /// Deposit object, identical to what CLI returns
    const depositObject = [
        {
            pubkey: Buffer.from(publicKey).toString('hex'),
            withdrawal_credentials: blsCredentialGeneratorConstants.WITHDRAWAL_CREDENTIALS,
            amount: AMOUNT,
            signature,
            deposit_message_root: depositMessageRoot,
            deposit_data_root: depositDataRoot,
            fork_version: blsCredentialGeneratorConstants.FORK_VERSION,
            network_name: blsCredentialGeneratorConstants.NETWORK_NAME,
            deposit_cli_version: blsCredentialGeneratorConstants.CLI_VERSION, /// Added to emulate deposit-cli
        }
    ];

    const keystore = await generateEncryptedFileStorage(bls, secretKey, password);

    return { depositObject, keystore };
};

module.exports = {
    _generateBLSCredentials,
    _getDepositDataFromKeystore
};
